// 서버 검증 거절(HTTP 400/422) 행 표식 — 재시도해도 결과가 같으므로 job을 끝내고 행에 표식을 남긴다.
// 표식은 fields_updated_at 안에 둔다(server_missing_at·need_restore와 같은 패턴).
// 결함 업로드의 fields_updated_at은 UPDATE_SHAPE_KEYS로 걸러 보내므로 표식은 서버로 나가지 않는다.
// 현재 대상은 결함(defects)만. 그 결함에 달린 사진·자식 결함은 표식 없이 부모 상태에서 파생한다.

export const SYNC_REJECTED_KEY = 'sync_rejected'
// 원인이 명확한 거절(빈 색상 등)을 자동 수리한 시각 — 수리는 1회만, 두 번째 거절은 표식으로 끝낸다
export const SYNC_REPAIRED_KEY = 'sync_repaired_at'
// 표식이 붙은 행도 이 시간이 지나면 1회 다시 보낸다 (서버 쪽 일시 오류로 붙은 표식이 영구화되지 않도록)
const SYNC_REJECTED_RETRY_MS = 6 * 60 * 60 * 1000
// 거절 뒤 처리(삭제·되돌림)가 네트워크 끊김 등 일시 오류로 멈춘 표식 — 연결되면 곧 다시 보내 처리를 마치도록 짧게 둔다
const SYNC_REJECTED_TRANSIENT_RETRY_MS = 60 * 1000

// transient: 거절 자체가 아니라 거절 뒤 처리(되돌릴 서버 값 조회 등)가 일시 오류로 실패해 보류한 표식
export type SyncRejection = { at: string; status: number; message: string; transient?: boolean }

// SQLite 집계용 조건 (JSON1 지원 여부와 무관하게 동작하도록 LIKE 사용, 저장 형식은 JSON.stringify)
// 일시 오류 보류(transient) 표식은 '저장 실패'가 아니라 '저장 대기'로 센다 — 주기 동기화가 다시 집어 가 처리를 마치도록
function rejectedSql(column: string): string {
  return `(${column} LIKE '%"${SYNC_REJECTED_KEY}"%' AND ${column} NOT LIKE '%"transient":true%')`
}
export const DEFECT_REJECTED_SQL = rejectedSql('fields_updated_at')
export const DEFECT_NOT_REJECTED_SQL = `(fields_updated_at IS NULL OR NOT ${DEFECT_REJECTED_SQL})`

// 부모 결함이 서버 미생성 + 거절 표식인 행은 '저장 대기'에서 뺀다 (사진·자식 결함은 부모를 다시 저장하면 함께 올라가므로
// 따로 실패로 세지 않는다). 부모가 수정(PUT) 거절인 경우는 서버 id가 있어 자식이 정상 업로드되므로 제외하지 않는다.
// @param parentRefColumn 바깥 행에서 부모 localStringId를 담은 컬럼 (테이블명으로 한정, 예: defect_photos.localShapeId)
function parentNotRejectedSql(parentRefColumn: string): string {
  return `NOT EXISTS (SELECT 1 FROM defects p WHERE p.localStringId = ${parentRefColumn} AND p.modifiedFromLocal = 1 AND instr(p.id, '_need_to_upload') > 0 AND ${rejectedSql('p.fields_updated_at')})`
}

/**
 * 테이블별 '저장 대기' 추가 조건 (없으면 빈 문자열, 있으면 ' AND ...' 형태)
 * @param table 대상 테이블
 * @param hasDefectsTable defects 테이블 존재 여부 (없으면 부모 조건을 붙이지 않는다)
 */
export function pendingExtraSql(table: string, hasDefectsTable: boolean): string {
  if (table === 'defects') {
    return ` AND ${DEFECT_NOT_REJECTED_SQL} AND ${parentNotRejectedSql('defects.parent_defect_id')}`
  }
  if (table === 'defect_photos' && hasDefectsTable) {
    return ` AND ${parentNotRejectedSql('defect_photos.localShapeId')}`
  }
  return ''
}

function toFieldUpdates(raw: unknown): Record<string, unknown> {
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {}
    } catch {
      return {}
    }
  }
  return typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
}

/**
 * 거절 표식이 있고 재시도 유예 중인 행인지 (동기화 재적재·job 생성을 건너뛸 대상)
 * @param row fields_updated_at을 가진 행 (문자열·객체 모두 허용)
 */
export function isSyncRejectedActive(row: { fields_updated_at?: unknown }): boolean {
  const marker = toFieldUpdates(row.fields_updated_at)[SYNC_REJECTED_KEY] as
    | Partial<SyncRejection>
    | undefined
  if (!marker) return false
  const at = new Date(marker.at ?? '').getTime()
  const retryMs = marker.transient ? SYNC_REJECTED_TRANSIENT_RETRY_MS : SYNC_REJECTED_RETRY_MS
  return isNaN(at) || Date.now() - at < retryMs
}

/**
 * 사용자가 행을 다시 저장할 때 거절·수리 표식을 지운 행을 돌려준다 (표식이 없으면 같은 객체)
 * @param row 저장할 행
 */
export function clearSyncRejection<T extends { fields_updated_at?: unknown }>(row: T): T {
  const fieldUpdates = toFieldUpdates(row.fields_updated_at)
  if (!(SYNC_REJECTED_KEY in fieldUpdates) && !(SYNC_REPAIRED_KEY in fieldUpdates)) return row
  const { [SYNC_REJECTED_KEY]: _rejected, [SYNC_REPAIRED_KEY]: _repaired, ...rest } = fieldUpdates
  return { ...row, fields_updated_at: rest }
}

/**
 * HTTP 상태가 서버 검증 거절인지 (재시도 무의미)
 * @param status HTTP 상태 코드
 */
export function isValidationRejectStatus(status: number | undefined): boolean {
  return status === 400 || status === 422
}

// 서버 결함 문자열 상한 (zentrix-api routes/defects.ts FIELD_TYPES와 맞출 것 — 서버 기본 상한은 255)
const DEFECT_STRING_MAX_LENGTH: Record<string, number> = {
  shape_type: 200,
  color: 50,
  memo: 400,
  structural_component: 200,
  classification: 200,
  inspection_type: 255,
  width: 200,
  length: 200,
  area: 200,
  count: 200,
  repair_plan: 200,
  cause_estimate: 200,
  template: 200,
  previous_balloon_number: 200,
  sort_key: 255,
}

// 서버 상한까지 자른다 (서버도 UTF-16 length로 재므로 같은 단위, 끝이 서로게이트 앞쪽이면 그 글자는 뺀다)
function truncateToServerLength(value: string, max: number): string {
  let cut = value.slice(0, max)
  const last = cut.charCodeAt(cut.length - 1)
  if (last >= 0xd800 && last <= 0xdbff) cut = cut.slice(0, -1)
  return cut
}

/**
 * 거절된 결함 값을 서버 규칙에 맞게 정리한다 (재전송 1회용). 문자열 상한 초과는 자르고, 숫자로 저장된 문자열 칸은 문자열로 바꾼다.
 * 빈 색상은 기본색이 캔버스·스토어 상태에 달려 있어 호출자가 따로 채운다.
 * @param row 거절된 결함 행
 * @returns 바뀐 필드와 값 (바꿀 게 없으면 빈 객체)
 */
export function sanitizeDefectForServer(row: Record<string, unknown>): Record<string, string> {
  const fixed: Record<string, string> = {}
  for (const [key, max] of Object.entries(DEFECT_STRING_MAX_LENGTH)) {
    const value = row[key]
    if (typeof value === 'number' && Number.isFinite(value)) {
      fixed[key] = truncateToServerLength(String(value), max)
    } else if (typeof value === 'string' && value.length > max) {
      fixed[key] = truncateToServerLength(value, max)
    }
  }
  return fixed
}
